import { hashKey, type QueryClient } from "@tanstack/vue-query";
import type { RouteLocationNormalized, Router } from "vue-router";
import api from "@/services/api";
import { featureFlagsKeys, type FeatureFlags } from "@/services/feature_flags";
import { isOrganizationMismatchError } from "@/services/queryRetry";
import {
  PROFILE_QUERY_KEY,
  resolveActiveOrganizationId,
  type Profile,
} from "@/services/user";

// The areas a page view can count toward: a subset of backend
// `activity.models.ActivityArea`, whose endpoint rejects anything else.
export type ActivityArea =
  | "home"
  | "agency_hub"
  | "dashboards"
  | "ad_performance"
  | "website"
  | "social"
  | "content"
  | "competitors"
  | "ai_visibility"
  | "goals"
  | "budgets"
  | "ecommerce"
  | "recommendations"
  | "automations"
  | "tags"
  | "data_sources"
  | "settings"
  | "tools";

// Every request made while a share link is showing is marked, so the app's
// own requests there (the profile, notices, flags) don't count as using the
// viewer's organization.
function isPublicShare(location: {
  meta: RouteLocationNormalized["meta"];
  query: RouteLocationNormalized["query"];
}): boolean {
  const share = location.meta.publicShare;
  return (
    share === "always" ||
    (share === "with-token" && Boolean(location.query.token))
  );
}

const PROFILE_HASH = hashKey(PROFILE_QUERY_KEY);

// Redirects and role guards move the route on well within this.
export const DWELL_MS = 2000;
// The profile and feature flags load in parallel with the first page.
const MAX_WAITS = 10;

type Readiness = "record" | "skip" | "wait";

function readiness(
  to: RouteLocationNormalized,
  profile: Profile | undefined,
  queryClient: QueryClient,
): Readiness {
  if (!profile) return "wait";
  const flag = to.meta.requiredFeatureFlag;
  if (!flag) return "record";
  const flags = queryClient.getQueryData<FeatureFlags>(
    featureFlagsKeys.flags({
      organizationId: resolveActiveOrganizationId(profile),
    }),
  );
  if (!flags) return "wait";
  // A paywalled page shows the sales pitch, and a consent-pending one the
  // consent form, not the feature.
  const status = flags[flag];
  return status?.enabled === true && status.consentPending !== true
    ? "record"
    : "skip";
}

export function installAreaViewTracking(
  router: Router,
  queryClient: QueryClient,
): () => void {
  // The server keeps one row per area per day; this only saves the request.
  const sent = new Set<string>();
  let timer: ReturnType<typeof setTimeout> | undefined;

  function attempt(to: RouteLocationNormalized, area: ActivityArea, waits = 0) {
    const profile = queryClient.getQueryData<Profile>(PROFILE_QUERY_KEY);
    const state = readiness(to, profile, queryClient);
    if (state === "wait") {
      if (waits < MAX_WAITS) {
        timer = setTimeout(() => attempt(to, area, waits + 1), DWELL_MS);
      }
      return;
    }
    if (state === "skip" || !profile) return;
    const organization = resolveActiveOrganizationId(profile);
    const day = new Date().toISOString().slice(0, 10);
    const key = `${day}:${profile.id}:${organization}:${area}`;
    if (sent.has(key)) return;
    sent.add(key);
    api
      .post("/activity/area-views/", { area }, { params: { organization } })
      .catch((error: Error) => {
        sent.delete(key);
        // Another tab moved the session. Refreshing the profile counts the
        // page again, for the organization it's now shown for.
        if (isOrganizationMismatchError(error)) {
          void queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
        }
      });
  }

  function schedule(to: RouteLocationNormalized) {
    clearTimeout(timer);
    const area = to.meta.activityArea;
    if (!area) return;
    timer = setTimeout(() => attempt(to, area), DWELL_MS);
  }

  // Read from the address bar now, since the app mounts and asks for the
  // profile before the first navigation's guards run; after that, set as
  // each navigation starts.
  let onPublicShare = isPublicShare(
    router.resolve(
      window.location.pathname + window.location.search + window.location.hash,
    ),
  );
  const stopBefore = router.beforeEach((to) => {
    onPublicShare = isPublicShare(to);
  });
  const interceptor = api.interceptors.request.use((config) => {
    if (onPublicShare) config.headers.set("X-Fyr-Public-Share", "1");
    return config;
  });

  // Any navigation that lands cancels the pending one, so only a page someone
  // stays on for the dwell time counts; an aborted one leaves it running.
  const stopAfter = router.afterEach((to, _from, failure) => {
    if (failure) {
      onPublicShare = isPublicShare(router.currentRoute.value);
      return;
    }
    schedule(to);
  });

  // Switching organization (or user) refreshes the page in place, with no
  // navigation, so the same page is a fresh view for the new scope.
  let scope: string | undefined;
  const stopWatching = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== "updated") return;
    if (event.query.queryHash !== PROFILE_HASH) {
      // Accepting consent or unlocking a feature opens it on the same page,
      // with no navigation. `readiness` re-checks the current organization's
      // flags, and `sent` drops a page already counted.
      if (
        event.action.type === "success" &&
        event.query.queryKey[0] === featureFlagsKeys.all[0] &&
        router.currentRoute.value.meta.requiredFeatureFlag
      ) {
        schedule(router.currentRoute.value);
      }
      return;
    }
    const profile = event.query.state.data as Profile | undefined;
    const next = profile
      ? `${profile.id}:${resolveActiveOrganizationId(profile)}`
      : undefined;
    if (next === scope) return;
    const previous = scope;
    scope = next;
    if (previous !== undefined && next !== undefined) {
      schedule(router.currentRoute.value);
    }
  });

  return () => {
    stopBefore();
    stopAfter();
    stopWatching();
    api.interceptors.request.eject(interceptor);
  };
}
